/**
 * Prefix used for filter-related keys in storage.
 */
export const FILTER_STORAGE_PREFIX = 'filter_';

/**
 * Prefix used for system-related keys in storage.
 */
export const SYSTEM_STORAGE_PREFIX = 'system_';

/**
 * Generates a storage prefix based on the given prefix and page identifier.
 *
 * @param {string} prefix - The prefix for the storage key (e.g., filter_ or system_).
 * @param {string} page - The page identifier to associate with the key.
 * @returns {string} The complete storage prefix including the page identifier.
 */
export const getStoragePrefixWithPage = (prefix: string, page: string): string =>
	`${prefix}${page}_`;

/**
 * Generates a filter storage prefix for a specific page.
 *
 * @param {string} page - The page identifier.
 * @returns {string} The filter storage prefix for the given page.
 */
export const getFilterStoragePrefixWithPage = (page: string): string =>
	getStoragePrefixWithPage(FILTER_STORAGE_PREFIX, page);

/**
 * Generates a filter storage key for a specific page and filter key.
 *
 * @param {string} page - The page identifier.
 * @param {string} key - The specific filter key.
 * @returns {string} The complete filter storage key.
 */
export const getFilterStorageKey = (page: string, key: string): string =>
	`${getFilterStoragePrefixWithPage(page)}${key}`;

/**
 * Generates a system storage prefix for a specific page.
 *
 * @param {string} page - The page identifier.
 * @returns {string} The system storage prefix for the given page.
 */
export const getSystemStoragePrefixWithPage = (page: string): string =>
	getStoragePrefixWithPage(SYSTEM_STORAGE_PREFIX, page);

/**
 * Generates a system storage key for a specific page and key.
 *
 * @param {string} page - The page identifier.
 * @param {string} key - The specific system key (e.g., "limit").
 * @returns {string} The complete system storage key.
 */
export const getSystemStorageKey = (page: string, key: string): string =>
	`${getSystemStoragePrefixWithPage(page)}${key}`;

/**
 * Retrieves a value from URL search parameters or session storage.
 * Checks URL parameters first, then falls back to session storage.
 *
 * @param {URLSearchParams} searchParams - The URL search parameters object.
 * @param {string} page - The page identifier.
 * @param {string} key - The key to look up in URL parameters or session storage.
 * @param {(page: string, key: string) => string} storageKeyGenerator - A function
 * to generate storage keys.
 * @returns {string | null} The value from URL parameters or session storage, or null if not found.
 */
export const getUrlOrSessionStorageValue = (
	searchParams: URLSearchParams,
	page: string,
	key: string,
	storageKeyGenerator: (page: string, key: string) => string,
): string | null => {
	const paramValue = searchParams.get(key);
	if (paramValue) return paramValue;

	const persistedKey = storageKeyGenerator(page, key);
	const persistedValue = sessionStorage.getItem(persistedKey);

	return persistedValue !== 'null' ? persistedValue : null;
};
